// Every tunable in Breeze lives here, with defaults. There is no settings
// screen yet, but the shape assumes one: an account's stored overrides get
// merged on top of DEFAULT_SETTINGS, never edited in place, so a fresh
// account with nothing stored still gets a complete, valid settings object.

export interface SimulcastLayer {
  /** A short name the SFU and the client both use to refer to this layer. */
  readonly name: "low" | "medium" | "high";
  readonly maxBitrateBps: number;
  readonly maxFramerate: number;
  /** 1 = full sender resolution, 2 = half width/height, 4 = quarter. */
  readonly scaleResolutionDownBy: number;
}

export interface VideoSettings {
  readonly maxWidth: number;
  readonly maxHeight: number;
  readonly maxFramerate: number;
  readonly simulcastLayers: readonly SimulcastLayer[];
}

export interface OriginalSoundSettings {
  /** Opus target average bitrate in bits per second (music, not voice). */
  readonly opusBitrateBps: number;
  readonly stereo: boolean;
  /** Discontinuous transmission. Off for music: silence still carries room tone. */
  readonly dtx: boolean;
  /** Forward error correction. */
  readonly fec: boolean;
  readonly sampleRateHz: number;
}

export interface RoomSettings {
  readonly maxParticipants: number;
  /** Whether a personal room defaults to holding guests in a knock queue. */
  readonly knockEnabledByDefault: boolean;
  /** Seconds a dropped participant is held as "reconnecting" before being
   * treated as left. */
  readonly reconnectGraceSeconds: number;
}

// The ten accent hues, light-mode values, verbatim from seamless's own
// palette (C:/dev/seamless/src/lib/colors.ts). Breeze carries no brand look
// of its own (BRIEF "Answers, 2026-09-27" — Adam, relayed from the seamless
// session: "breeze needs no breeze copy for the user"), so the one
// per-account preference left is which of these an account picks, and this
// is the one place that mapping lives — apps/web reads it to paint --accent
// and to render the swatches in its accent picker.
export const ACCENT_HUES = {
  blue: "#2563eb",
  indigo: "#4f46e5",
  violet: "#7c3aed",
  pink: "#db2777",
  red: "#dc2626",
  orange: "#ea580c",
  amber: "#b45309",
  green: "#059669",
  teal: "#0d9488",
  cyan: "#0284c7",
} as const;

export type AccentHue = keyof typeof ACCENT_HUES;

export const ACCENT_HUE_KEYS = Object.keys(ACCENT_HUES) as readonly AccentHue[];

export function isAccentHue(value: string): value is AccentHue {
  return value in ACCENT_HUES;
}

/** How the whole app is colored: light, dark, or whichever the device is set to. */
export const APPEARANCES = ["light", "dark", "auto"] as const;

export type Appearance = (typeof APPEARANCES)[number];

export function isAppearance(value: unknown): value is Appearance {
  return typeof value === "string" && (APPEARANCES as readonly string[]).includes(value);
}

export interface ThemeSettings {
  readonly accent: AccentHue;
}

export interface ScreenShareSettings {
  readonly maxWidth: number;
  readonly maxHeight: number;
  readonly maxFramerate: number;
  readonly maxBitrateBps: number;
}

export interface MeterSettings {
  readonly freeGbPerMonth: number;
  readonly warnAtPercent: number;
  readonly blockAtPercent: number;
}

export interface RingSettings {
  readonly ringTimeoutSeconds: number;
}

export interface BreezeSettings {
  readonly video: VideoSettings;
  readonly screenShare: ScreenShareSettings;
  readonly originalSound: OriginalSoundSettings;
  readonly room: RoomSettings;
  readonly theme: ThemeSettings;
  readonly meter: MeterSettings;
  readonly ring: RingSettings;
}

export const DEFAULT_SETTINGS: BreezeSettings = {
  video: {
    maxWidth: 1280,
    maxHeight: 720,
    maxFramerate: 30,
    simulcastLayers: [
      { name: "low", maxBitrateBps: 250_000, maxFramerate: 20, scaleResolutionDownBy: 3 },
      { name: "medium", maxBitrateBps: 900_000, maxFramerate: 30, scaleResolutionDownBy: 1.5 },
      { name: "high", maxBitrateBps: 2_500_000, maxFramerate: 30, scaleResolutionDownBy: 1 },
    ],
  },
  screenShare: {
    maxWidth: 1920,
    maxHeight: 1080,
    maxFramerate: 10,
    maxBitrateBps: 2_500_000,
  },
  originalSound: {
    opusBitrateBps: 256_000,
    stereo: true,
    dtx: false,
    fec: true,
    sampleRateHz: 48_000,
  },
  room: {
    maxParticipants: 8,
    knockEnabledByDefault: true,
    reconnectGraceSeconds: 30,
  },
  theme: {
    accent: "blue",
  },
  meter: {
    freeGbPerMonth: 1000,
    warnAtPercent: 80,
    blockAtPercent: 95,
  },
  ring: {
    ringTimeoutSeconds: 45,
  },
};

/** Deep-merges a partial override onto the defaults. Missing branches keep
 * their default value; present branches replace their leaves, not their
 * whole parent object, so a settings screen can patch one field. */
export function resolveSettings(overrides?: DeepPartial<BreezeSettings>): BreezeSettings {
  if (!overrides) return DEFAULT_SETTINGS;
  return {
    video: { ...DEFAULT_SETTINGS.video, ...overrides.video },
    screenShare: { ...DEFAULT_SETTINGS.screenShare, ...overrides.screenShare },
    originalSound: { ...DEFAULT_SETTINGS.originalSound, ...overrides.originalSound },
    room: { ...DEFAULT_SETTINGS.room, ...overrides.room },
    theme: { ...DEFAULT_SETTINGS.theme, ...overrides.theme },
    meter: { ...DEFAULT_SETTINGS.meter, ...overrides.meter },
    ring: { ...DEFAULT_SETTINGS.ring, ...overrides.ring },
  };
}

type DeepPartial<T> = { [K in keyof T]?: Partial<T[K]> };

/** The options passed to `getDisplayMedia` when someone shares their screen.
 * `selfBrowserSurface: "exclude"` keeps the call's own tab out of the picker,
 * because sharing it mirrors the call into itself and shows a host's private
 * panel to the room. Kept here, as plain data, so a test can assert on it. */
export function screenShareOptions(settings: ScreenShareSettings = DEFAULT_SETTINGS.screenShare) {
  return {
    video: {
      width: { ideal: settings.maxWidth },
      height: { ideal: settings.maxHeight },
      frameRate: { ideal: settings.maxFramerate },
    },
    audio: false,
    selfBrowserSurface: "exclude",
  } as const;
}
